import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, Trash2, X, CloudUpload } from "lucide-react";
import { cn } from "../../utils/cn";

interface ConfirmModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    message: React.ReactNode;
    variant?: 'danger' | 'warning' | 'info';
    confirmLabel?: string;
    cancelLabel?: string;
    isLoading?: boolean;
}

export function ConfirmModal({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    variant = 'danger',
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    isLoading = false
}: ConfirmModalProps) {
    if (!isOpen) return null;

    const variants = {
        danger: {
            iconBg: "bg-red-100 dark:bg-red-900/30",
            iconColor: "text-red-600 dark:text-red-500",
            borderColor: "border-red-100 dark:border-red-900/20",
            buttonBg: "bg-red-600 hover:bg-red-700",
            icon: AlertTriangle
        },
        warning: {
            iconBg: "bg-orange-100 dark:bg-orange-900/30",
            iconColor: "text-orange-600 dark:text-orange-500",
            borderColor: "border-orange-100 dark:border-orange-900/20",
            buttonBg: "bg-orange-600 hover:bg-orange-700",
            icon: AlertTriangle
        },
        info: {
            iconBg: "bg-siemens-petrol/10",
            iconColor: "text-siemens-petrol",
            borderColor: "border-siemens-deep-blue/10 dark:border-siemens-deep-blue/20",
            buttonBg: "bg-siemens-petrol hover:bg-siemens-deep-blue",
            icon: CloudUpload
        }
    };

    const style = variants[variant];
    const Icon = style.icon;

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 z-[70]"
                        onClick={onClose}
                    />
                    {/* Modal */}
                    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ type: "spring", stiffness: 300, damping: 30 }}
                            className={cn(
                                "w-full max-w-sm bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden pointer-events-auto border",
                                style.borderColor
                            )}
                        >
                            <div className="p-6 text-center">
                                <div className={cn("w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4", style.iconBg)}>
                                    <Icon className={cn("w-6 h-6", style.iconColor)} />
                                </div>

                                <h3 className="text-lg font-bold text-siemens-deep-blue dark:text-white mb-2">
                                    {title}
                                </h3>

                                <div className="text-sm text-siemens-text-soft mb-6">
                                    {message}
                                </div>

                                <div className="flex gap-3">
                                    <button
                                        onClick={onClose}
                                        disabled={isLoading}
                                        className="flex-1 h-10 text-sm font-semibold text-siemens-deep-blue dark:text-white bg-slate-100 dark:bg-slate-800 rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
                                    >
                                        {cancelLabel}
                                    </button>
                                    <button
                                        onClick={onConfirm}
                                        disabled={isLoading}
                                        className={cn(
                                            "flex-1 h-10 text-sm font-semibold text-white rounded transition-colors flex items-center justify-center gap-2 shadow-lg shadow-current/20 disabled:opacity-50",
                                            style.buttonBg
                                        )}
                                    >
                                        {isLoading ? "Processing..." : confirmLabel}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
}
